/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { FeaturePolygonData } from "@/types/content";
type PolygonProps = NonNullable<FeaturePolygonData["properties"]>;
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { autobind } from "core-decorators";
import EditorPolygonController from "@/components/mapbox/Lib/Editors/EditorPolygonController";
import { FeatureTypeEnum } from "@/types/enum";
import { MapboxCustomObject } from "./MapboxCustomObject";
import * as mapboxgl from "mapbox-gl";
import {
  buffer,
  polygon as turfPolygon,
  polygonToLine,
  rhumbBearing,
  rhumbDestination,
} from "@turf/turf";
import { MAPBOX_MIN_ZOOM } from "@/constant";
import useProjectStore from "@/stores/useProjectStore";
import { getSunHours } from "../Utils/ObjectUtils";
import { calculateAnnualSunHoursForFeature } from "../Shading/shadeProfile";
import type { GeoJSONFeature } from "mapbox-gl";

export class MapboxPolygonObject extends MapboxCustomObject {
  public _editor: EditorPolygonController;
  private _isEditing: boolean = false;
  private _isDragHighlighted: boolean = false;
  private _segmentLabelsSourceId: string | null = null;
  private _segmentLabelsLayerId: string | null = null;
  public _selectedVertex?: {
    type: "corner" | "midpoint";
    index?: number;
    insertIndex?: number;
    featureId?: string;
  };
  private _isEditingOffset: boolean = false;

  constructor(mapboxViewer: MapboxViewer, content: FeaturePolygonData) {
    super(mapboxViewer, content);
    this._editor = this._mapboxViewer.editors.editorPolygonController!;
    this.init(content);
  }

  get editor() {
    return this._editor;
  }

  @autobind
  getClearanceAreaFeature(): GeoJSON.Feature | null {
    if (!this._content.coordinates || this._content.coordinates.length < 3) {
      return null;
    }

    if (!this.visible) {
      return null;
    }
    const projectClearanceDistance =
      useProjectStore.getState().project?.clearanceDistance || 3;

    const clearanceDistanceMeters = Math.max(
      this._content.properties?.clearanceDistance || 0,
      projectClearanceDistance,
    );
    const show = this._content.properties?.showClearanceArea || false;
    if (!show || clearanceDistanceMeters <= 0) {
      return null;
    }

    const processedCoordinates = this.processPolygonCoordinates(
      this._content.coordinates,
    );
    if (!processedCoordinates || processedCoordinates.length < 3) {
      return null;
    }

    try {
      const ringCoordinates = processedCoordinates as [number, number][];

      const isCW = this.isClockwise(ringCoordinates);
      const perpTurn = isCW ? 90 : -90; // outward: right for CCW, left for CW

      const bands: number[][][][] = [];

      for (let i = 0; i < ringCoordinates.length - 1; i++) {
        const a = ringCoordinates[i];
        const b = ringCoordinates[i + 1];
        // Skip zero-length segments
        if (Math.abs(a[0] - b[0]) < 1e-12 && Math.abs(a[1] - b[1]) < 1e-12) {
          continue;
        }

        const segBearing = rhumbBearing(
          {
            type: "Feature",
            geometry: { type: "Point", coordinates: a },
            properties: {},
          } as GeoJSON.Feature<GeoJSON.Point>,
          {
            type: "Feature",
            geometry: { type: "Point", coordinates: b },
            properties: {},
          } as GeoJSON.Feature<GeoJSON.Point>,
        );
        const outwardBearing = segBearing - perpTurn;

        const aOffPoint = rhumbDestination(
          {
            type: "Feature",
            geometry: { type: "Point", coordinates: a },
            properties: {},
          } as GeoJSON.Feature<GeoJSON.Point>,
          clearanceDistanceMeters,
          outwardBearing,
          { units: "meters" },
        );
        const bOffPoint = rhumbDestination(
          {
            type: "Feature",
            geometry: { type: "Point", coordinates: b },
            properties: {},
          } as GeoJSON.Feature<GeoJSON.Point>,
          clearanceDistanceMeters,
          outwardBearing,
          { units: "meters" },
        );

        const aOff = (aOffPoint.geometry as GeoJSON.Point)
          .coordinates as number[];
        const bOff = (bOffPoint.geometry as GeoJSON.Point)
          .coordinates as number[];

        // Build a rectangle ring with perpendicular connectors and parallel opposite edge
        const rectRing: number[][] = [
          [a[0], a[1]],
          [b[0], b[1]],
          [bOff[0], bOff[1]],
          [aOff[0], aOff[1]],
          [a[0], a[1]],
        ];

        bands.push([rectRing]);
      }

      if (bands.length === 0) return null;

      const clearanceGeometry: GeoJSON.MultiPolygon = {
        type: "MultiPolygon",
        coordinates: bands,
      };

      return {
        type: "Feature",
        id: `${this._contentId}-clearance` as string,
        geometry: clearanceGeometry,
        properties: {
          app: "logistic-app",
          contentId: this._contentId,
          color: "#ff0000",
          visible: this.visible,
          clearanceDistance: clearanceDistanceMeters,
          showClearanceArea: true,
        },
      } as GeoJSON.Feature;
    } catch {
      return null;
    }
  }

  @autobind
  getClearanceLinesFeatures(): GeoJSON.Feature[] {
    if (!this._content.coordinates || this._content.coordinates.length < 3) {
      return [];
    }
    if (!this.visible) return [];

    const projectClearanceDistance =
      useProjectStore.getState().project?.clearanceDistance || 3;
    const clearanceDistanceMeters = Math.max(
      this._content.properties?.clearanceDistance || 0,
      projectClearanceDistance,
    );
    const show = this._content.properties?.showClearanceArea || false;
    if (!show || clearanceDistanceMeters <= 0) {
      return [];
    }

    const processedCoordinates = this.processPolygonCoordinates(
      this._content.coordinates,
    );
    if (!processedCoordinates || processedCoordinates.length < 3) {
      return [];
    }

    const ringCoordinates = processedCoordinates as [number, number][];
    const isCW = this.isClockwise(ringCoordinates);
    const perpTurn = isCW ? 90 : -90; // outward: right for CCW, left for CW

    const features: GeoJSON.Feature[] = [];

    for (let i = 0; i < ringCoordinates.length - 1; i++) {
      const a = ringCoordinates[i];
      const b = ringCoordinates[i + 1];
      if (Math.abs(a[0] - b[0]) < 1e-12 && Math.abs(a[1] - b[1]) < 1e-12) {
        continue;
      }

      const segBearing = rhumbBearing(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: a },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: b },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
      );
      const outwardBearing = segBearing - perpTurn;

      const aOffPoint = rhumbDestination(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: a },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        clearanceDistanceMeters,
        outwardBearing,
        { units: "meters" },
      );
      const bOffPoint = rhumbDestination(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: b },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        clearanceDistanceMeters,
        outwardBearing,
        { units: "meters" },
      );
      const aOff = (aOffPoint.geometry as GeoJSON.Point)
        .coordinates as number[];
      const bOff = (bOffPoint.geometry as GeoJSON.Point)
        .coordinates as number[];

      // Offset edge line
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [aOff, bOff] },
        properties: {
          app: "logistic-app",
          contentId: this._contentId,
          color: "#ff0000",
          visible: this.visible,
          clearanceDistance: clearanceDistanceMeters,
          showClearanceArea: true,
        },
      });

      // Perpendicular connector lines at both ends: from original to offset
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [a, aOff] },
        properties: {
          app: "logistic-app",
          contentId: this._contentId,
          color: "#ff0000",
          visible: this.visible,
          clearanceDistance: clearanceDistanceMeters,
          showClearanceArea: true,
        },
      });
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [b, bOff] },
        properties: {
          app: "logistic-app",
          contentId: this._contentId,
          color: "#ff0000",
          visible: this.visible,
          clearanceDistance: clearanceDistanceMeters,
          showClearanceArea: true,
        },
      });
    }

    return features;
  }

  set visible(visible: boolean) {
    this._content.config.visible = visible;
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  get visible() {
    return this._content.config.visible;
  }

  set height(height: number) {
    if (!this._content.properties) {
      this._content.properties = {};
    }
    this._content.properties.height = height;
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  get height() {
    return this._content.properties?.height !== undefined
      ? this._content.properties?.height
      : 10;
  }

  @autobind
  setEditingOffset(editing: boolean) {
    this._isEditingOffset = editing;
    // Trigger vertices refresh
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  get isEditingOffset() {
    return this._isEditingOffset;
  }

  @autobind
  init(content: FeaturePolygonData) {
    this._content = content;
    this._contentId = `${content.id}`;
    // Add to shared entity instead of creating individual source
    this._mapboxViewer.entities.polygonEntity?.add(this);

    // Create segment labels layer
    this._segmentLabelsSourceId = `${this._contentId}-segments-source`;
    this._segmentLabelsLayerId = `${this._contentId}-segments-layer`;

    // Add source and layer for segment labels
    if (content.coordinates && content.coordinates.length > 1) {
      const segmentLabels = this.getSegmentLabels(content.coordinates);

      this._mapboxViewer._mapViewer.addSource(this._segmentLabelsSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: segmentLabels.map((label) => ({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: label.coordinates,
            },
            properties: {
              length: label.length,
            },
          })),
        },
        minzoom: MAPBOX_MIN_ZOOM,
      });

      this._mapboxViewer._mapViewer.addLayer({
        id: this._segmentLabelsLayerId,
        type: "symbol",
        source: this._segmentLabelsSourceId,
        layout: {
          "text-field": ["get", "length"],
          "text-size": 12,
          "text-anchor": "center",
          "text-offset": [0, -0.5],
          visibility:
            content.config?.visible &&
            content.properties?.showSegmentLabels !== false
              ? "visible"
              : "none",
        },
        paint: {
          "text-color": content.config?.color || "#8b5cf6",
          "text-halo-color": "#ffffff",
          "text-halo-width": 2,
        },
      });
    }
  }

  @autobind
  getFeature(): GeoJSON.Feature | null {
    if (!this._content.coordinates || this._content.coordinates.length < 3) {
      return null;
    }

    // Process coordinates to ensure proper polygon format
    const processedCoordinates = this.processPolygonCoordinates(
      this._content.coordinates,
    );

    if (!processedCoordinates || processedCoordinates.length < 3) {
      return null;
    }

    return {
      type: "Feature",
      id: this._contentId,
      geometry: {
        type: "Polygon",
        coordinates: [processedCoordinates], // Properly nested array for polygon exterior ring
      },
      properties: {
        contentId: this._contentId,
        app: "logistic-app",
        color: this._content.config.color || "#8b5cf6",
        name: this._content.name || "Polygon",
        category: FeatureTypeEnum.POLYGON,
        isDragging: this._isDragHighlighted,
        visible: this.visible, // Add visibility property
        height:
          this._content.properties?.height !== undefined
            ? this._content.properties?.height
            : 10, // Add height property for extrusion
        base_height: 0,
        isViolatingRestrictions:
          this._content.properties?.isViolatingRestrictions || false,
      },
    };
  }

  @autobind
  getVertexFeatures(): GeoJSON.Feature[] {
    if (!this._content.coordinates || this._content.coordinates.length < 3) {
      return [];
    }

    const coordinates = this._content.coordinates;
    const features: GeoJSON.Feature[] = [];

    // Only create vertex features if polygon is visible
    if (!this.visible || !this._isEditing) {
      return [];
    }

    // If editing offset, expose offset vertices instead of main polygon
    if (
      this._isEditingOffset &&
      this._content.properties?.isManualOffset &&
      Array.isArray(this._content.properties?.offsetCoordinates) &&
      this._content.properties!.offsetCoordinates.length > 1
    ) {
      const offsetCoords = this._content.properties!.offsetCoordinates;
      for (let i = 0; i < offsetCoords.length - 1; i++) {
        const coord = offsetCoords[i];
        features.push({
          type: "Feature",
          id: `${this._contentId}-offset-vertex-${i}`,
          geometry: {
            type: "Point",
            coordinates: coord,
          },
          properties: {
            app: "logistic-app",
            polygonId: this._contentId,
            vertexType: "corner",
            vertexIndex: i,
            category: "polygon-vertex",
            isOffsetVertex: true,
            visible: this.visible,
          },
        });
      }
      return features;
    }

    // Create vertex points for each corner (excluding the closing point)
    for (let i = 0; i < coordinates.length - 1; i++) {
      const coord = coordinates[i];
      features.push({
        type: "Feature",
        id: `${this._contentId}-vertex-${i}`,
        geometry: {
          type: "Point",
          coordinates: coord,
        },
        properties: {
          app: "logistic-app",
          polygonId: this._contentId,
          vertexType: "corner",
          vertexIndex: i,
          category: "polygon-vertex",
          visible: this.visible, // Add visibility property
        },
      });
    }

    // Always create midpoint vertices when editing (not just when _isEditing flag is true)
    if (this._isEditing) {
      for (let i = 0; i < coordinates.length - 1; i++) {
        const current = coordinates[i];
        const next = coordinates[(i + 1) % (coordinates.length - 1)];
        const midpoint = [
          (current[0] + next[0]) / 2,
          (current[1] + next[1]) / 2,
        ];

        features.push({
          type: "Feature",
          id: `${this._contentId}-midpoint-${i}`,
          geometry: {
            type: "Point",
            coordinates: midpoint,
          },
          properties: {
            app: "logistic-app",
            polygonId: this._contentId,
            vertexType: "midpoint",
            insertIndex: i + 1, // Where to insert the new vertex
            category: "polygon-vertex",
            visible: this.visible, // Add visibility property
          },
        });
      }
    }

    return features;
  }

  @autobind
  getOffsetLineFeature(): GeoJSON.Feature | null {
    if (!this._content.coordinates || this._content.coordinates.length < 3) {
      return null;
    }

    const offsetDistanceMeters = this._content.properties?.offsetDistance || 0;
    if (!this.visible) {
      return null;
    }

    const processedCoordinates = this.processPolygonCoordinates(
      this._content.coordinates,
    );
    if (!processedCoordinates || processedCoordinates.length < 3) {
      return null;
    }

    try {
      let buffered: GeoJSON.Feature<
        GeoJSON.Polygon | GeoJSON.MultiPolygon,
        GeoJSON.GeoJsonProperties
      > | null = null;
      // Build buffer around the polygon and convert to an outline (no fill)
      if (this._content.properties.isManualOffset) {
        const offsetCoordinates = this._content.properties.offsetCoordinates;
        if (!offsetCoordinates || offsetCoordinates.length < 3) {
          return null;
        }
        buffered = turfPolygon([offsetCoordinates]);
      } else {
        if (offsetDistanceMeters <= 0) {
          return null;
        }
        const ringCoordinates = processedCoordinates as [number, number][];
        const basePolygon = turfPolygon([ringCoordinates]);
        const offsetM = -offsetDistanceMeters;
        buffered = buffer(basePolygon, offsetM, {
          units: "meters",
        }) as GeoJSON.Feature<
          GeoJSON.Polygon | GeoJSON.MultiPolygon,
          GeoJSON.GeoJsonProperties
        > | null;
      }
      if (!buffered || !buffered.geometry) return null;

      const outline = polygonToLine(
        buffered as unknown as GeoJSON.Feature<
          GeoJSON.Polygon | GeoJSON.MultiPolygon
        >,
      ) as unknown as GeoJSON.Feature<
        GeoJSON.LineString | GeoJSON.MultiLineString
      >;

      if (!outline || !outline.geometry) return null;

      return {
        type: "Feature",
        id: `${this._contentId}-offset`,
        geometry: outline.geometry,
        properties: {
          app: "logistic-app",
          contentId: this._contentId,
          color: this._content.config.color || "#8b5cf6",
          visible: this.visible,
          offsetDistance: offsetDistanceMeters,
          isManualOffset: !!this._content.properties?.isManualOffset,
          isViolatingRestrictions:
            this._content.properties?.isViolatingRestrictions || false,
        },
      } as GeoJSON.Feature;
    } catch {
      return null;
    }
  }

  /**
   * Process polygon coordinates to ensure proper format and avoid rendering issues
   */
  @autobind
  private processPolygonCoordinates(coordinates: number[][]): number[][] {
    if (!coordinates || coordinates.length < 3) {
      return [];
    }

    // Create a copy to avoid mutating original data
    let processedCoords = coordinates.map((coord) => [
      // Ensure proper precision (limit to ~1cm precision)
      Math.round(coord[0] * 1000000) / 1000000,
      Math.round(coord[1] * 1000000) / 1000000,
    ]);

    // Remove duplicate consecutive points (except for closure)
    processedCoords = this.removeDuplicatePoints(processedCoords);

    // Ensure minimum 3 points for a valid polygon
    if (processedCoords.length < 3) {
      return [];
    }

    // Ensure polygon is properly closed (first point equals last point)
    const firstPoint = processedCoords[0];
    const lastPoint = processedCoords[processedCoords.length - 1];

    if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
      processedCoords.push([firstPoint[0], firstPoint[1]]);
    }

    // Ensure counter-clockwise winding order (Mapbox standard)
    if (this.isClockwise(processedCoords)) {
      processedCoords.reverse();
    }

    // Validate polygon doesn't have self-intersections at critical points
    processedCoords = this.validatePolygonGeometry(processedCoords);

    return processedCoords;
  }

  /**
   * Remove duplicate consecutive points while preserving polygon closure
   */
  @autobind
  private removeDuplicatePoints(coordinates: number[][]): number[][] {
    if (coordinates.length <= 1) return coordinates;

    const result: number[][] = [coordinates[0]];

    for (let i = 1; i < coordinates.length; i++) {
      const current = coordinates[i];
      const previous = result[result.length - 1];

      // Only add if not duplicate (with small tolerance for floating point precision)
      if (
        Math.abs(current[0] - previous[0]) > 0.000001 ||
        Math.abs(current[1] - previous[1]) > 0.000001
      ) {
        result.push(current);
      }
    }

    return result;
  }

  /**
   * Check if polygon coordinates are in clockwise order
   */
  @autobind
  private isClockwise(coordinates: number[][]): boolean {
    let area = 0;
    const n = coordinates.length;

    for (let i = 0; i < n - 1; i++) {
      const j = (i + 1) % n;
      area +=
        (coordinates[j][0] - coordinates[i][0]) *
        (coordinates[j][1] + coordinates[i][1]);
    }

    return area > 0;
  }

  /**
   * Validate and fix basic polygon geometry issues
   */
  @autobind
  private validatePolygonGeometry(coordinates: number[][]): number[][] {
    // Check for minimum area (avoid zero-area polygons that can cause rendering issues)
    const area = this.calculatePolygonArea(coordinates);

    // If polygon area is too small, it might cause rendering issues at high zoom
    if (Math.abs(area) < 0.000000001) {
      // Create a small buffer around the polygon to ensure it's visible
      return this.createMinimumViablePolygon(coordinates);
    }

    return coordinates;
  }

  /**
   * Calculate polygon area using shoelace formula
   */
  @autobind
  private calculatePolygonArea(coordinates: number[][]): number {
    let area = 0;
    const n = coordinates.length;

    for (let i = 0; i < n - 1; i++) {
      area += coordinates[i][0] * coordinates[i + 1][1];
      area -= coordinates[i + 1][0] * coordinates[i][1];
    }

    return area / 2;
  }

  /**
   * Create a minimum viable polygon when original is too small
   */
  @autobind
  private createMinimumViablePolygon(coordinates: number[][]): number[][] {
    if (coordinates.length < 2) return coordinates;

    // Calculate centroid
    const centroid = this.calculateCentroid(coordinates);

    // Create a small square around the centroid (about 1 meter at equator)
    const offset = 0.00001; // Roughly 1 meter

    return [
      [centroid[0] - offset, centroid[1] - offset],
      [centroid[0] + offset, centroid[1] - offset],
      [centroid[0] + offset, centroid[1] + offset],
      [centroid[0] - offset, centroid[1] + offset],
      [centroid[0] - offset, centroid[1] - offset], // Close the polygon
    ];
  }

  /**
   * Calculate polygon centroid
   */
  @autobind
  private calculateCentroid(coordinates: number[][]): number[] {
    let x = 0,
      y = 0;
    const n = coordinates.length - 1; // Exclude the closing point

    for (let i = 0; i < n; i++) {
      x += coordinates[i][0];
      y += coordinates[i][1];
    }

    return [x / n, y / n];
  }

  private calculateDistance(
    point1: [number, number],
    point2: [number, number],
  ): number {
    const R = 6371000; // Earth's radius in meters
    const lat1 = (point1[1] * Math.PI) / 180;
    const lat2 = (point2[1] * Math.PI) / 180;
    const deltaLat = ((point2[1] - point1[1]) * Math.PI) / 180;
    const deltaLng = ((point2[0] - point1[0]) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) *
        Math.cos(lat2) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  private formatLength(length: number): string {
    if (length >= 1000) {
      return `${(length / 1000).toFixed(2)} km`;
    } else {
      return `${length.toFixed(2)} m`;
    }
  }

  private getSegmentLabels(
    coords: number[][],
  ): { coordinates: [number, number]; length: string }[] {
    const labels: { coordinates: [number, number]; length: string }[] = [];

    for (let i = 0; i < coords.length - 1; i++) {
      const start = coords[i] as [number, number];
      const end = coords[i + 1] as [number, number];
      const length = this.calculateDistance(start, end);

      // Calculate midpoint for label placement
      const midpoint: [number, number] = [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2,
      ];

      labels.push({
        coordinates: midpoint,
        length: this.formatLength(length),
      });
    }

    return labels;
  }

  @autobind
  updateContent(content: FeaturePolygonData) {
    this._content = content;
    // Trigger source update through entity
    this._mapboxViewer.entities.polygonEntity?.updateSource();

    // Update segment labels
    if (
      this._segmentLabelsSourceId &&
      content.coordinates &&
      content.coordinates.length > 1
    ) {
      const segmentLabels = this.getSegmentLabels(content.coordinates);
      const source = this._mapboxViewer._mapViewer.getSource(
        this._segmentLabelsSourceId,
      ) as mapboxgl.GeoJSONSource;

      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: segmentLabels.map((label) => ({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: label.coordinates,
            },
            properties: {
              length: label.length,
            },
          })),
        });
      }

      // Update layer visibility and color
      if (this._segmentLabelsLayerId) {
        this._mapboxViewer._mapViewer.setLayoutProperty(
          this._segmentLabelsLayerId,
          "visibility",
          content.config?.visible &&
            content.properties?.showSegmentLabels !== false
            ? "visible"
            : "none",
        );
        this._mapboxViewer._mapViewer.setPaintProperty(
          this._segmentLabelsLayerId,
          "text-color",
          content.config?.color || "#8b5cf6",
        );
      }
    }
  }

  @autobind
  setHighlight() {
    // Implement highlight logic using feature state
    this._mapboxViewer._mapViewer.setFeatureState(
      { source: "polygon-source", id: this._contentId },
      { highlight: true },
    );
  }

  @autobind
  setUnhighlight() {
    this._mapboxViewer._mapViewer.removeFeatureState(
      { source: "polygon-source", id: this._contentId },
      "highlight",
    );
  }

  @autobind
  setSelected() {
    this._mapboxViewer._mapViewer.setFeatureState(
      { source: "polygon-source", id: this._contentId },
      { selected: true },
    );
  }

  @autobind
  setViolationRestrictions(isViolation: boolean) {
    if (!this._content.properties) {
      this._content.properties = {};
    }
    this._content.properties.isViolatingRestrictions = isViolation;
    this._mapboxViewer._mapViewer.setFeatureState(
      { source: "polygon-source", id: this._contentId },
      { isViolatingRestrictions: isViolation },
    );
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  @autobind
  setEditing(editing: boolean) {
    this._isEditing = editing;
    if (editing) {
      this._mapboxViewer.entities.polygonEntity?.showVertices();
    } else {
      this._mapboxViewer.entities.polygonEntity?.hideVertices();
    }
    // Update vertex features
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  @autobind
  updateVertex(vertexIndex: number, newCoordinate: number[]) {
    if (
      !this._content.coordinates ||
      vertexIndex < 0 ||
      vertexIndex >= this._content.coordinates.length - 1
    ) {
      return;
    }

    const newCoordinates = [...this._content.coordinates];
    newCoordinates[vertexIndex] = newCoordinate;

    // Update the closing point if we're moving the first vertex
    if (vertexIndex === 0) {
      newCoordinates[newCoordinates.length - 1] = newCoordinate;
    }

    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  @autobind
  updateOffsetVertex(vertexIndex: number, newCoordinate: number[]) {
    const props = this._content.properties;
    if (
      !props?.isManualOffset ||
      !Array.isArray(props?.offsetCoordinates) ||
      vertexIndex < 0 ||
      vertexIndex >= (props!.offsetCoordinates?.length || 1) - 1
    ) {
      return;
    }

    const newCoords = [...(props!.offsetCoordinates as number[][])];
    newCoords[vertexIndex] = newCoordinate;
    if (vertexIndex === 0) {
      newCoords[newCoords.length - 1] = newCoordinate;
    }

    this._content.properties = {
      ...(props || {}),
      offsetCoordinates: newCoords,
    } as PolygonProps;
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  @autobind
  insertVertex(insertIndex: number, newCoordinate: number[]) {
    if (
      !this._content.coordinates ||
      insertIndex < 0 ||
      insertIndex > this._content.coordinates.length - 1
    ) {
      return;
    }

    const newCoordinates = [...this._content.coordinates];
    newCoordinates.splice(insertIndex, 0, newCoordinate);

    // Update the closing point to match the first point
    newCoordinates[newCoordinates.length - 1] = newCoordinates[0];

    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  @autobind
  removeVertex(vertexIndex: number) {
    if (
      !this._content.coordinates ||
      this._content.coordinates.length <= 4 ||
      vertexIndex < 0 ||
      vertexIndex >= this._content.coordinates.length - 1
    ) {
      return; // Don't allow removal if it would make an invalid polygon
    }

    const newCoordinates = [...this._content.coordinates];
    newCoordinates.splice(vertexIndex, 1);

    // Update the closing point
    newCoordinates[newCoordinates.length - 1] = newCoordinates[0];

    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  @autobind
  dispose() {
    // Remove segment labels layer and source
    if (
      this._segmentLabelsLayerId &&
      this._mapboxViewer._mapViewer.getLayer(this._segmentLabelsLayerId)
    ) {
      this._mapboxViewer._mapViewer.removeLayer(this._segmentLabelsLayerId);
    }
    if (
      this._segmentLabelsSourceId &&
      this._mapboxViewer._mapViewer.getSource(this._segmentLabelsSourceId)
    ) {
      this._mapboxViewer._mapViewer.removeSource(this._segmentLabelsSourceId);
    }
    this._segmentLabelsSourceId = null;
    this._segmentLabelsLayerId = null;

    this._mapboxViewer.entities.polygonEntity?.remove(this._contentId);
  }

  @autobind
  movePolygon(newCoordinates: number[][]) {
    // Calculate translation delta relative to current coordinates
    const prevFirst = this._content.coordinates?.[0];
    const nextFirst = newCoordinates?.[0];
    if (
      prevFirst &&
      nextFirst &&
      this._content.properties?.isManualOffset &&
      Array.isArray(this._content.properties?.offsetCoordinates) &&
      this._content.properties!.offsetCoordinates.length > 0
    ) {
      const deltaLng = (nextFirst[0] as number) - (prevFirst[0] as number);
      const deltaLat = (nextFirst[1] as number) - (prevFirst[1] as number);
      // Translate manual offset ring by the same delta
      const translated = this._content.properties!.offsetCoordinates.map(
        (coord: number[]) => [
          (coord[0] as number) + deltaLng,
          (coord[1] as number) + deltaLat,
        ],
      );
      this._content.properties!.offsetCoordinates = translated;
    }

    // Temporarily update coordinates for real-time feedback
    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  @autobind
  setDragHighlight(highlighted: boolean) {
    this._isDragHighlighted = highlighted;

    // Update feature state for visual feedback
    this._mapboxViewer._mapViewer.setFeatureState(
      { source: "polygon-source", id: this._contentId },
      { dragging: highlighted },
    );
  }

  @autobind
  focusOn() {
    const coordinates = this._content.coordinates;
    if (!coordinates || coordinates.length === 0) return;

    const bounds = coordinates.reduce((bounds, coord) => {
      return bounds.extend(coord as mapboxgl.LngLatLike);
    }, new mapboxgl.LngLatBounds());
    this._mapboxViewer._mapViewer.fitBounds(bounds);
  }

  @autobind
  async calculateSunHours(stepLng: number = 0.00002, stepLat?: number) {
    const shadeMap = this._mapboxViewer?.shadeMap;
    const coords = this._content?.coordinates;

    if (!shadeMap || !Array.isArray(coords) || coords.length < 3) {
      return 0;
    }

    const ring = this.processPolygonCoordinates(coords);
    if (!ring || ring.length < 3) return 0;
    const openRing = ring.slice(0, -1);
    const targetFeature = this.getFeature() as GeoJSONFeature | null;

    return getSunHours(
      shadeMap,
      openRing,
      stepLng,
      stepLat,
      targetFeature ?? undefined,
    );
  }

  @autobind
  async getSunHoursAtPoint(): Promise<number> {
    const coords = this._content?.coordinates;
    const center = this.calculateCentroid(coords) as [number, number];
    const shadeMap = this._mapboxViewer?.shadeMap;
    const targetFeature = this.getFeature() as GeoJSONFeature | null;
    if (!shadeMap || !targetFeature) return 0;

    const features = await shadeMap.getShadingFeatures();
    return calculateAnnualSunHoursForFeature(
      targetFeature,
      features,
      { lat: center[1], lng: center[0] },
    );
  }
}
